import { REQUIRED_MESSAGES } from '@srm/ui/lib/requiredMessage';
import './OfferApproveToStageTwo.scss';
import { useEffect, useMemo, useState } from 'react';
import Button from '@srm/ui/button';
import clsx from 'clsx';
import Modal from '@srm/ui/modal';
import { IOffer } from '@models/winnerSelection/ITableDemand';
import { Controller, useForm } from 'react-hook-form';
import { useUnsavedFormInput } from '@srm/ui/hooks/useUnsavedFormInput';
import { IOption } from '@srm/ui/types/IOption';
import { WinnerSelectionService } from '@services/WinnerSelectionService';
import { sendNotification } from '@srm/ui/lib/notifications';
import Hint from '@srm/ui/hint';
import { IOfferApproveToStageTwoFormData } from './IOfferApproveToStageTwoFormData';
import FormProviderControl from '@controls/FormProviderControl';
import HookFormProvider from '@srm/ui/controls/HookFormProvider';
import SelectControl from '@srm/ui/controls/SelectControl';

export interface IOfferApproveToStageTwoProps {
    tableKey: string;
    isEdit: boolean;
    value: IOffer;
    demandId: string;
    onSubmit?: (value: IOfferApproveToStageTwoFormData) => void;
    onClose?: () => void;
}

const OfferApproveToStageTwo = ({
    tableKey,
    isEdit,
    value,
    demandId,
    onSubmit,
    onClose,
}: IOfferApproveToStageTwoProps) => {
    const [error, setError] = useState<string>();
    const methods = useForm<IOfferApproveToStageTwoFormData>({
        mode: 'onBlur',
    });
    const hasUnsavedInput = useUnsavedFormInput(methods);

    const { isSubmitting, isDirty } = methods.formState;

    useEffect(() => {
        const defaultValue: IOfferApproveToStageTwoFormData = {
            approveToStageTwo: value.approveToStageTwo,
        };
        methods.reset(defaultValue);
    }, [value]);

    const onFormSubmit = async (form: IOfferApproveToStageTwoFormData) => {
        setError(undefined);
        return WinnerSelectionService.saveTableDemandOfferApproveToStageTwo(value.key, demandId, tableKey, form)
            .then(() => {
                sendNotification({
                    message: 'Данные успешно сохранены',
                    variant: 'green',
                });
                onSubmit && onSubmit(form);
            })
            .catch((reason) => {
                setError(reason);
                console.log(reason);
            });
    };

    const results = useMemo(() => {
        return [
            { label: 'Допущен', value: 1 },
            { label: 'Не допущен', value: 0 },
        ] as IOption[];
    }, []);

    const errorLabel = error && (
        <div className="offer-st2__error">
            <Hint role="alert" title={error} tone="danger" />
        </div>
    );

    const modalFooter = (
        <div className={'modal-dlg-buttons'}>
            <Button
                type="submit"
                form="offer-st2-form"
                loading={isSubmitting}
                className={clsx('offer-st2__button', 'offer-st2__save-button')}
                disabled={isSubmitting || !isDirty}
            >
                Сохранить
            </Button>
        </div>
    );

    return (
        <Modal
            size="m"
            onClose={onClose}
            hasUnsavedInput={() => isEdit && hasUnsavedInput()}
            header={`Допуск ко второму этапу предложения №${value.number || ''} (${value.name})`}
            footer={isEdit ? modalFooter : null}
        >
            {errorLabel}
            <FormProviderControl
                formMethods={methods}
                id="offer-st2-form"
                className="offer-st2__form"
                onSubmit={onFormSubmit}
            >
                <HookFormProvider controller={Controller}>
                    <div className="offer-st2__content">
                        <div className="offer-st2__row">
                            <SelectControl
                                label="Допуск ко второму этапу"
                                name="approveToStageTwo"
                                readOnly={true}
                                disabled={!isEdit}
                                highlightMenuItems={false}
                                options={results}
                                control={methods.control}
                                required={true}
                                rules={{ required: REQUIRED_MESSAGES.select }}
                                setValue={(options) => {
                                    return options[0].value;
                                }}
                                getValue={(options: IOption[], value: any) => {
                                    return options.filter((x) => x.value === value);
                                }}
                            />
                        </div>
                    </div>
                </HookFormProvider>
            </FormProviderControl>
        </Modal>
    );
};

export default OfferApproveToStageTwo;
